/* NotFoundPage — branded 404 with quick routes back into the product. */
import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Compass, Home, ArrowRight, Search } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEOHead from '../components/SEOHead';

const QUICK_LINKS = [
  { to: '/career-clarity', label: 'After 10th / Inter' },
  { to: '/specializations', label: 'Inner Specializations' },
  { to: '/job-roles', label: 'Future-Ready Job Roles' },
  { to: '/pathways/internships', label: 'Internship Pathfinder' },
  { to: '/pathways/fresher-launchpad', label: 'Fresher Launchpad' },
  { to: '/pathways/career-switch-growth', label: 'Career Switch & Growth' },
];

export default function NotFoundPage() {
  const { pathname } = useLocation();
  return (
    <div className="min-h-screen bg-[#FFF9F1] flex flex-col">
      <SEOHead title="Page not found · LearnixTa" description="This page doesn't exist — but your career path does. Head back to LearnixTa." />
      <Navbar />
      <main className="flex-1 pt-28 md:pt-32 pb-16 px-4 md:px-6" data-testid="not-found-page">
        <div className="max-w-2xl mx-auto text-center">
          <div className="mx-auto w-16 h-16 rounded-2xl bg-teal-50 border-2 border-teal-100 flex items-center justify-center mb-6">
            <Compass size={28} className="text-teal-600" />
          </div>
          <p className="text-[11px] font-extrabold tracking-widest uppercase text-teal-700 mb-2">Error 404</p>
          <h1 className="text-4xl sm:text-5xl font-black text-gray-900 mb-3">
            This path doesn&rsquo;t exist.
          </h1>
          <p className="text-gray-600 text-base max-w-md mx-auto mb-2">
            Yours does — it&rsquo;s just not at this address. The page you&rsquo;re looking for was moved, renamed, or never existed.
          </p>
          <p className="text-xs text-gray-400 font-mono mb-8 break-all" data-testid="not-found-path">{pathname}</p>

          <div className="flex flex-col sm:flex-row gap-3 justify-center mb-10">
            <Link to="/" data-testid="not-found-home"
              className="inline-flex items-center justify-center gap-2 bg-teal-600 hover:bg-teal-700 text-white font-extrabold px-6 py-3 rounded-full transition-colors">
              <Home size={16} /> Back to homepage
            </Link>
            <Link to="/dashboard" data-testid="not-found-pricing"
              className="inline-flex items-center justify-center gap-2 border-2 border-gray-300 hover:bg-white text-gray-700 font-bold px-6 py-3 rounded-full transition-colors">
              Explore paths <ArrowRight size={15} />
            </Link>
          </div>

          <div className="bg-white rounded-3xl border-2 border-teal-100 p-6 text-left" data-testid="not-found-quicklinks">
            <p className="flex items-center gap-2 text-[11px] font-extrabold tracking-widest uppercase text-gray-500 mb-4">
              <Search size={13} className="text-teal-600" /> Popular pathways
            </p>
            <div className="flex flex-wrap gap-2">
              {QUICK_LINKS.map((l) => (
                <Link key={l.to} to={l.to} data-testid={`not-found-link-${l.to.split('/').pop()}`}
                  className="px-4 py-2 rounded-full border border-teal-200 bg-teal-50/50 hover:bg-teal-100 text-teal-800 text-sm font-bold transition-colors">
                  {l.label}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
